import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import arrowDown from "@/assets/icn/arrowDown.png";
import Image from "next/image";
import MetaSection from "@/elements/MetaSection/MetaSection";
import { cn, capitalized } from "@/lib/utils";

function generateMetaText(compKeyword, symbolKeyword) {
    try {
        if (!compKeyword || !symbolKeyword) {
            return "";
        }

        return "? industries,# stock,? share,? stock,# Share,? shares,? stocks,# Shares,# stocks,? NSE,? BSE,NSE ?,? industries share,? industries shares,# share price,? share price,? industries stocks,? industries stock,# stock price,# stocks price,? stock price,? stocks price,? shares price,? Share Market,? price NSE,? share NSE,? share BSE,? shares NSE,? shares BSE,? stock NSE,? stock BSE,? stocks NSE,? stocks BSE,# stock NSE,# stock BSE,# stocks NSE,# stocks BSE,? Stock Market,Buy ? Share,Buy ? Shares,Buy ? Stock,Buy ? Stocks,# Shares Price,About ? Share,About ? Shares,About ? Stock,About ? Stocks,? industries share price,# share price today,? share price today,? Shares Price Today,? Stock Price Today,? Stocks Price Today,? industries share NSE,? industries share BSE,# shares price today,# Stock Price Today,# Stocks Price Today,cost of ? share,current market price of ? industries,current price of ? industries,current price of ? industries share,current price of ? share,current price of #,current price of # share,current rate of ? industries,current rate of ? industries share,current rate of ? share,current rate of # share,current ? share price,current share price of ?,current share price of ? industries,current share price of ? industries limited,current share price of ? industries ltd,current share price of #,current share rate of ? industries,current share value of ? industries,current stock price of ? industries,current value of ? share"
            .replaceAll("?", compKeyword)
            .replaceAll("#", symbolKeyword);
    } catch (error) {
        console.error("Error generating meta text:", error);
        return "";
    }
}

export default function StockFAQSection({
    data,
    keyIndicatorCardData,
    isPaidUserPage,
    stockFAQData,
    stocksData,
    stockDetails
}) {
    const [active, setActive] = useState(-1);
    const router = useRouter();

    const faqData = [
        {
            question: `Is ${data?.compName} a good stock to invest in?`,
            answer: `Our expert verdict states that ${data?.compName} is in ${data?.shortTermVerdict} zone for Short term and is in ${data?.longTermVerdict} zone for Long term.`
        },
        {
            question: `Should I buy ${data?.compName} share now?`,
            answer: `As per our verdict, ${data?.compName} is in the ${data?.shortTermVerdict} zone for Short term.`
        },
        {
            question: `Should I buy ${data?.compName} shares for the long term?`,
            answer: `As per our verdict, ${data?.compName} is in ${data?.longTermVerdict} zone for Long term.`
        },
        {
            question: `What is ${data?.compName} share price today?`,
            answer: `The ${data?.compName} share price today is ${data?.nseLtpPrice ?? data?.bseLtpPrice}.`
        },
        {
            question: `How to buy ${data?.compName} share?`,
            answer: `You can buy ${data?.compName} shares on any demat broker by completing your KYC. Before buying any stock, check BUY/SELL/HOLD insights on the Univest App.`
        },
        {
            question: `What is the share price of ${data?.compName}?`,
            answer: `The share price of ${data?.compName} is ${
                data?.nseLtpPrice ?? data?.bseLtpPrice
            }, please note that the price may vary on a real-time basis. `
        },
        {
            question: `What are the 52 Week's High and Low of ${data?.compName}?`,
            answer: `52 Week High of ${data?.compName} is ${stockFAQData?.price52WeekHigh} and 52 Week Low of ${data?.compName} is ${stockFAQData?.price52WeekLow}.`
        },
        {
            question: `What is the PE and PB ratio of ${data?.compName}?`,
            answer: `PE ratio of ${data?.compName} is ${keyIndicatorCardData?.peRatio} and PB ratio of ${data?.compName} is ${keyIndicatorCardData?.pbRatio}.`
        },
        {
            question: `How’s PE of ${data?.compName} compared to its sector?`,
            answer: `PE ratio of ${data?.compName} is ${keyIndicatorCardData?.peRatio} whereas the sector PE ratio is ${keyIndicatorCardData?.sectorPE}.`
        },
        {
            question: `What is the market cap of ${data?.compName}?`,
            answer: `${data?.compName}’s market cap is ${keyIndicatorCardData?.marketCapital}.`
        },
        {
            question: `What are today’s High and Low prices of ${data?.compName} ? `,
            answer: `Today’s High of ${data?.compName} is ${
                data?.nseTodaysHigh ?? data?.bseTodaysHigh
            }.<br /> Today’s Low of ${data?.compName} is ${data?.nseTodaysLow ?? data?.bseTodaysLow}.`
        }
    ];

    const compKeyword = data?.compName ?? "";
    const symbolKeyword = stockDetails?.symbol ?? "";

    const metaText = generateMetaText(compKeyword, symbolKeyword);

    return (
        <>
            {stocksData?.BODY?.map((ele, j) => (
                <div key={j} className="font-Inter mx-4 my-8 block">
                    {ele.title && <h2 className="text-lg leading-8 font-extrabold">{ele.title}</h2>}
                    {ele.disc && <p className="text-sm leading-6 font-medium text-[#414141]">{ele.disc}</p>}
                    {ele.list && (
                        <ul className="font-Inter list-decimal pl-5 list-outside text-justify">
                            {ele.list?.map((ele, i) => (
                                <li
                                    key={i}
                                    className="text-sm leading-6 font-normal text-[#414141]"
                                    dangerouslySetInnerHTML={{ __html: ele }}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            ))}
            <MetaSection
                title={
                    stocksData?.META?.title ??
                    `${data?.compName} (${data?.title}) Share/Stock Price Today Live BSE/NSE | Univest `
                }
                desc={
                    stocksData?.META?.desc ??
                    `${stockDetails?.symbol} Share/Stock price today Live NSE/BSE - Get ${stockDetails?.symbol} stock price with Fundamentals, Performance, Profit & Loss, and Latest Insights.`
                }
                keyWords={metaText}
                schema={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "BreadcrumbList",
                            "@id": `https://univest.in${router?.asPath}`,
                            itemListElement: [
                                {
                                    "@type": "ListItem",
                                    position: 2,
                                    name: "Stock",
                                    item: `https://univest.in${router?.asPath}`
                                }
                            ]
                        },
                        {
                            "@type": "Organization",
                            "@id": "https://univest.in/stocks/#organization",
                            url: "https://univest.in/stocks",
                            name: "Univest",
                            logo: "https://storage.googleapis.com/app-assets-univest/website-assets/UnivestLogo.jpg",
                            address: {
                                "@type": "PostalAddress",
                                addressLocality: "Gurugram, Haryana",
                                postalCode: "122002",
                                streetAddress: "10, Poorvi Marg, DLF Phase 2, Sector 25, Gurugram, Haryana 122002"
                            },
                            email: "support@univest.in",
                            telephone: "+91-8800049790",
                            sameAs: [
                                "https://www.facebook.com/Univest.in",
                                "https://www.instagram.com/univest.in/",
                                "https://twitter.com/UnivestIN",
                                "http://linkedin.com/company/univest-in",
                                "https://www.youtube.com/channel/UCRlFx-WxA4yY7Y33SRZaQ4w",
                                "https://www.crunchbase.com/organization/univest-557f"
                            ],
                            contactPoint: [
                                {
                                    "@type": "ContactPoint",
                                    telephone: "+91-8800049790",
                                    contactType: "Customer Service"
                                }
                            ],
                            foundingDate: "2022",
                            founders: [
                                {
                                    "@type": "Person",
                                    name: "Pranit Arora",
                                    id: "https://in.linkedin.com/in/pranit-arora-1b505422"
                                },
                                {
                                    "@type": "Person",
                                    name: "Avneet Dhamija",
                                    id: "https://in.linkedin.com/in/avneet-dhamija"
                                },
                                {
                                    "@type": "Person",
                                    name: "Vikash Agarwal",
                                    id: "https://in.linkedin.com/in/vikashagrawal1985"
                                }
                            ],
                            funder: {
                                "@type": "Corporation",
                                legalName: "Trinity media Group",
                                url: "https://www.vccircle.com/company/trinity-media-group"
                            }
                        },
                        {
                            "@type": "FAQPage",
                            "@id": "https://univest.in/#faq",
                            mainEntity: faqData?.map((ele) => ({
                                "@type": "Question",
                                name: ele.question,
                                acceptedAnswer: {
                                    "@type": "Answer",
                                    text: ele.answer
                                }
                            }))
                        },
                        {
                            "@type": "Product",
                            "@id": "https://univest.in/stocks/#product",
                            name: "Stock Advisory service In India",
                            url: "https://univest.in/stocks",
                            image: "",
                            review: [
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "I like Today's Gainers and Today's Losers in Discover in the Invest section.It helps me plan which Stocks should I invest and which I should not",
                                    author: { "@type": "Person", name: "Baradi Shivakumar" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "I like the Portfolio News in the Invest section on the univest App. It shows News related to the Stocks that are in my Portfolio which helps me in keeping a track of my Stocks status",
                                    author: { "@type": "Person", name: "Naman Nema" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "I like the option chain section and the way Greeks are displayed as it is very simple to track and make decision to execute orders. It is also easy to buy and sell positions, UI is very nice.",
                                    author: { "@type": "Person", name: "Shiva singh" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2022"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody: "Where trust matters univest never breaks.",
                                    author: { "@type": "Person", name: "Jatin" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "The ROS feature is very good and can help the budding option traders to learn with proper discipline and risk management.",
                                    author: { "@type": "Person", name: "Pandi Donald" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "I have been using univest for over 5 years now and it has provided me with an awesome experience and excellent customer support. Thanks to univest, I have also gained a lot of knowledge about stock market.",
                                    author: { "@type": "Person", name: "Prabhakar Dutta" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                },
                                {
                                    "@type": "Review",
                                    reviewBody:
                                        "I like the Portfolio News in the Invest section on the univest App. It shows News related to the Stocks that are in my Portfolio which helps me in keeping a track of my Stocks status",
                                    author: { "@type": "Person", name: "Pandi Donald" },
                                    reviewRating: { "@type": "Rating", ratingValue: "4.9" },
                                    datePublished: "2023"
                                }
                            ],
                            brand: "univest",
                            description:
                                "Start Trading in Share Market, SIP, IPOs, Mutual Fund, Indices and Commodity at univest.in with hassle free process.",
                            aggregateRating: {
                                "@type": "AggregateRating",
                                ratingValue: "4.4",
                                ratingCount: "230120",
                                reviewCount: "197000"
                            }
                        },
                        {
                            "@type": "WebPage",
                            "@id": "https://univest.in/#webpage",
                            name:
                                stocksData?.META?.title ??
                                `${stockDetails?.symbol} Share/Stock Price Today Live BSE/NSE | Univest `,
                            description:
                                stocksData?.META?.desc ??
                                `${stockDetails?.symbol} Share/Stock price today Live NSE/BSE - Get ${stockDetails?.symbol} stock price with Fundamentals, Performance, Profit & Loss, and Latest Insights.`,
                            keywords:
                                "bombay stock exchange, Indian stock market, bse, national stock exchange, Nifty, nse, sensex, stock exchange, stock market, stock research tool, smart investment, investment ideas, stock market tips",
                            speakable: {
                                "@type": "SpeakableSpecification",
                                cssSelector: [
                                    stocksData?.META?.title ??
                                        `${stockDetails?.symbol} Share/Stock Price Today Live BSE/NSE | Univest `,
                                    stocksData?.META?.desc ??
                                        `${stockDetails?.symbol} Share/Stock price today Live NSE/BSE - Get ${stockDetails?.symbol} stock price with Fundamentals, Performance, Profit & Loss, and Latest Insights.`
                                ]
                            },
                            url: `https://univest.in${router?.asPath}`
                        }
                    ]
                }}
            />
            <div className={cn("h-20", { hidden: !isPaidUserPage })} />
            <section className={cn("my-12 font-Inter mx-4", { hidden: isPaidUserPage })}>
                <h2 className="font-Inter text-[16px] text-black mr-4 font-semibold">
                    {capitalized(data?.nseSymbol ?? data?.bseSymbol)} FAQs
                </h2>
                <div className="flex flex-col py-8 gap-4 lg:gap-8">
                    {faqData?.map((ele, i) => (
                        <FAQcard
                            key={i}
                            heading={ele.question}
                            content={ele.answer}
                            active={active}
                            i={i}
                            setActive={setActive}
                        />
                    ))}
                </div>
            </section>
        </>
    );
}

function FAQcard({ heading, content, active, setActive, i }) {
    if (
        heading.includes("undefined") ||
        content.includes("undefined") ||
        heading.includes("null") ||
        content.includes("null")
    )
        return <></>;
    return (
        <div
            className={`ease-in-out duration-300 border rounded-xl cursor-pointer bg-white ${
                active === i ? "border-[#00439D] border-2" : "border-[#dfdfdf]"
            } `}
            onClick={() => setActive(active === i ? -1 : i)}
        >
            <div className="flex flex-row p-3">
                <div className="mr-3 mt-1.5 h-1 w-1 lg:h-2 lg:w-2 bg-[#202020] rounded-full" />
                <h3 className="w-10/12 font-semibold text-xs lg:text-sm">{heading}</h3>
                <Image
                    src={arrowDown}
                    className={`ml-auto mt-1.5 h-2 lg:h-2.5 w-auto mr-2 ease-in-out duration-300 ${
                        active === i ? "rotate-180" : "rotate-0"
                    } `}
                    alt="arrow"
                />
            </div>
            <div
                className={`overflow-hidden ease-in-out duration-300 mx-4 text-justify ml-8
            ${active === i ? "max-h-96 opacity-100" : "max-h-0 opacity-0"} `}
            >
                <p
                    className="mb-4 mt-2 font-normal text-xs lg:text-sm text-[#202020]"
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            </div>
        </div>
    );
}
